/**
 * Get the effective price per unit for an ad. Uses metadata.price_per_unit (exact string from form)
 * when present so values like 0.0055 are not rounded by the DB price column.
 * @param {object} ad - Ad object with price and optional metadata.price_per_unit
 * @returns {number} Price as number for calculations
 */
export function getEffectivePrice(ad) {
  if (!ad) return 0;
  // Metadata can be an object or a JSON string (e.g. from Supabase JSONB)
  let meta = ad.metadata;
  if (meta && typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch (e) {
      meta = null;
    }
  }
  const precise = meta?.price_per_unit;
  if (precise != null && precise !== '') {
    const num = parseFloat(precise);
    if (!Number.isNaN(num)) return num;
  }
  return parseFloat(ad.price) || 0;
}

/**
 * Format a price number for display so small decimals (e.g. 0.0095) are shown correctly
 * instead of rounding to 0.00000. Uses enough decimal places based on the value.
 * @param {number|string} value - Price value (will be parsed to number)
 * @param {number} [minDecimals=2] - Minimum decimal places to show
 * @param {number} [maxDecimals=8] - Maximum decimal places to show
 * @returns {string} Formatted price string
 */
export function formatPriceDecimals(value, minDecimals = 2, maxDecimals = 8) {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (value === '' || value === null || value === undefined || Number.isNaN(num)) {
    return minDecimals > 0 ? '0.' + '0'.repeat(minDecimals) : '0';
  }
  if (num === 0) {
    return minDecimals > 0 ? '0.' + '0'.repeat(minDecimals) : '0';
  }
  const abs = Math.abs(num);
  let decimals;
  if (abs < 0.0001) decimals = Math.min(8, maxDecimals);
  else if (abs < 0.01) decimals = Math.min(6, maxDecimals);
  else if (abs < 1) decimals = Math.min(4, maxDecimals);
  else decimals = Math.min(minDecimals, maxDecimals);
  let s = num.toFixed(decimals);
  // Strip trailing zeros (and trailing decimal point) so 0.0055 stays "0.0055" not "0.005500"
  if (s.includes('.')) {
    s = s.replace(/0+$/, '').replace(/\.$/, '');
  }
  return s;
}
